import { FC } from "react";
import {
  Box,
  List,
  ListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import JsonEditor from "@/components/general/jsonEditor";
import styles from "@/components/general/jsonBuilder.module.css";
import { useTranslation } from "react-i18next";

interface GenericJsonBuilderWrapperProps {
  addNewEntry: () => void;
  resetEntry: (index: number) => void;
  deleteEntry: (index: number) => void;
  jsonEntries: any[];
  renderItems: (entry: any, index: number) => React.ReactNode;
  hideEntries?: (entry: any, index: number) => boolean;
  enableDragDrop?: boolean;
  onReorder?: (result: DropResult) => void;
  transformedJson: any;
  enableSaveButton?: boolean;
  onSave?: (updatedJson: object | string) => void;
  label?: string;
  required?: boolean;
  secondaryButton?: {
    label: string;
    onClick: () => void;
  };
  topInfoBarContent?: string;
  topInfoBarClassName?: string;
  enableDeleteButton?: boolean;
  enableResetButton?: boolean;
  enableAddNewEntryButton?: boolean;
  entryLayout?: "vertical" | "horizontal";
}

const GenericJsonBuilderWrapper: FC<GenericJsonBuilderWrapperProps> = ({
  addNewEntry,
  resetEntry,
  deleteEntry,
  jsonEntries,
  renderItems,
  enableDragDrop = false,
  onReorder,
  transformedJson,
  enableSaveButton = false,
  onSave,
  label,
  required,
  secondaryButton,
  topInfoBarContent,
  topInfoBarClassName,
  enableResetButton,
  enableDeleteButton,
  enableAddNewEntryButton,
  hideEntries,
  entryLayout = "vertical",
}) => {
  const { t } = useTranslation();
  const actionButtonSx =
    entryLayout === "horizontal" ? { alignSelf: "flex-start" } : undefined;

  return (
    <Box className={styles.mainContainer}>
      <Box className={styles.headerSection}>
        <Typography variant="h5">
          {label || t("json_builder")}
          {required && (
            <span className={styles.labelRequired}>{"\u{002A}"}</span>
          )}
        </Typography>
        <Box className={styles.actionButtonsWrapper}>
          {secondaryButton && (
            <Button color="primary" onClick={secondaryButton?.onClick}>
              {secondaryButton?.label}
            </Button>
          )}
          {enableAddNewEntryButton && (
            <Button
              variant="contained"
              color="primary"
              onClick={addNewEntry}
              data-validation-focus-target
            >
              {t("new_entry")}
            </Button>
          )}
        </Box>
      </Box>

      {topInfoBarContent && (
        <Box className={styles.topInfoBar}>
          <Typography variant="subtitle1" className={topInfoBarClassName}>
            {topInfoBarContent}
          </Typography>
        </Box>
      )}

      <Box className={styles.entryListWrapper}>
        <List
          className={styles.entryList}
          sx={{
            ".MuiList-root": {
              padding: 0,
            },
          }}
        >
          {enableDragDrop && onReorder ? (
            <DragDropContext
              onDragEnd={(result) => onReorder && onReorder(result)}
            >
              <Droppable droppableId="jsonBuilderEntries">
                {(provided) => (
                  <List {...provided.droppableProps} ref={provided.innerRef}>
                    {jsonEntries.map(
                      (entry, index) =>
                        (hideEntries ? !hideEntries(entry, index) : true) && (
                          <Draggable
                            key={index}
                            draggableId={`entry-${index}`}
                            index={index}
                          >
                            {(provided) => (
                              <ListItem
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={styles.entryItem}
                              >
                                <IconButton
                                  {...provided.dragHandleProps}
                                  className={styles.dragHandle}
                                  aria-label={t("drag_entry")}
                                >
                                  <DragIndicatorIcon />
                                </IconButton>
                                <Box className={styles.entryContent}>
                                  {renderItems(entry, index)}
                                </Box>
                                {enableResetButton && (
                                  <IconButton
                                    title={t("reset_entry")}
                                    onClick={() => resetEntry(index)}
                                    sx={actionButtonSx}
                                  >
                                    <ClearIcon />
                                  </IconButton>
                                )}
                                {enableDeleteButton && (
                                  <IconButton
                                    title={t("delete_entry")}
                                    onClick={() => deleteEntry(index)}
                                    sx={actionButtonSx}
                                  >
                                    <DeleteIcon color="error" />
                                  </IconButton>
                                )}
                              </ListItem>
                            )}
                          </Draggable>
                        ),
                    )}
                    {provided.placeholder}
                  </List>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            jsonEntries.map(
              (entry, index) =>
                (hideEntries ? !hideEntries(entry, index) : true) && (
                  <ListItem key={index} className={styles.entryItem}>
                    <Box className={styles.entryContent}>
                      {renderItems(entry, index)}
                    </Box>
                    {enableResetButton && (
                      <IconButton
                        title={t("reset_entry")}
                        onClick={() => resetEntry(index)}
                        sx={actionButtonSx}
                      >
                        <ClearIcon />
                      </IconButton>
                    )}
                    {enableDeleteButton && (
                      <IconButton
                        title={t("delete_entry")}
                        onClick={() => deleteEntry(index)}
                        sx={actionButtonSx}
                      >
                        <DeleteIcon color="error" />
                      </IconButton>
                    )}
                  </ListItem>
                ),
            )
          )}
        </List>

        <Box
          sx={{
            ".MuiAccordionSummary-root": {
              padding: "0 0.5rem",
            },
            ".MuiAccordionDetails-root": {
              padding: 0,
            },
            "& .MuiPaper-root": {
              maxWidth: "100%",
            },
          }}
        >
          <Accordion
            defaultExpanded={false}
            className={styles.jsonPreviewAccordion}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="large" />}>
              <Typography variant="subtitle1">
                {t("generated_json_preview")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <JsonEditor
                  noLabel
                  readonly
                  value={transformedJson}
                  enableCopyButton
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {enableSaveButton && (
          <Box className={styles.saveButtonWrapper}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => onSave && onSave(transformedJson)}
            >
              {t("save")}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default GenericJsonBuilderWrapper;
